/**
 * One-time community founding (moderator only) and invite acceptance
 * (invited members). Both end by writing the membership into the user's
 * Community List — the vault every Concord client (including Armada) folds.
 */

import { useMutation } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import type { NostrEvent } from "@nostrify/nostrify";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/useToast";
import { COMMUNITY_RELAYS } from "@/lib/concord/config";
import {
  foundCommunity,
  membershipFromBundle,
  publishGuestbookEntry,
} from "@/lib/concord/ops";
import { writeMembership, type CommunityListState } from "@/lib/concord/membership";
import type { InviteBundle, Membership } from "@/lib/concord/types";

export function useCommunityActions(opts: {
  membership: Membership | undefined;
  communityList: CommunityListState;
  onChanged: () => void;
}) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const communitySink = {
    event: (e: NostrEvent) =>
      nostr.group(COMMUNITY_RELAYS).event(e, { signal: AbortSignal.timeout(8000) }),
  };

  const found = useMutation({
    mutationFn: async (input: { name: string; description: string }) => {
      if (!user) throw new Error("You must be logged in");
      const { membership } = await foundCommunity({
        signer: user.signer,
        ownerPub: user.pubkey,
        name: input.name,
        description: input.description,
        sink: communitySink,
      });
      await writeMembership(
        {
          signer: user.signer,
          pubkey: user.pubkey,
          membership,
          state: opts.communityList,
        },
        nostr,
      );
      // The founder announces themselves on the Guestbook too.
      await publishGuestbookEntry({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        verb: "join",
        sink: communitySink,
      });
      return membership;
    },
    onSuccess: () => {
      toast({ title: "The chama is live", description: "Your community has been founded." });
      opts.onChanged();
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Couldn't found the community",
        description: "Something went wrong. Try again.",
        variant: "destructive",
      });
    },
  });

  const acceptInvite = useMutation({
    mutationFn: async (bundle: InviteBundle) => {
      if (!user) throw new Error("You must be logged in");
      const membership = membershipFromBundle(bundle);
      await writeMembership(
        {
          signer: user.signer,
          pubkey: user.pubkey,
          membership,
          state: opts.communityList,
        },
        nostr,
      );
      // Announce the join on the Guestbook (CORD-02 §5).
      await publishGuestbookEntry({
        signer: user.signer,
        authorPub: user.pubkey,
        membership,
        verb: "join",
        sink: communitySink,
      });
      return membership;
    },
    onSuccess: () => {
      toast({ title: "Karibu!", description: "Welcome to the chama." });
      opts.onChanged();
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Couldn't accept the invite",
        description: "Something went wrong. Try again.",
        variant: "destructive",
      });
    },
  });

  return { found, acceptInvite };
}
